import { useEffect, useMemo, useRef, useState } from 'react';
import type { HeaderProps } from '@stitch-fix/knit';
import { useRouter } from 'next/router';
import { IconFixDelivery16, useScreenSize } from '@stitch-fix/mode-react';
import { useLocalStorageValue } from '@react-hookz/web';
import { primaryLandingPaths } from './adultsIncentiveBannerSupportedPaths';
import { supFixIncentiveBannerSupportedPaths } from './supFixIncentiveBannerSupportedPaths';
import { type BannerLocalStorageData, type IncentiveBannerTypes, knitBannersLocalStorageKey, type UseIncentiveBannerProps } from '../types';
export const TwentyDollarIncentiveCopy = {
  id: 'AdultsFirstFixDiscountBanner-20dollar',
  text: 'Limited time First Fix offer! $20 off + waived styling fee',
  Icon: IconFixDelivery16
};
export const TwentyPercentIncentiveCopy = {
  id: 'AdultsFirstFixDiscountBanner-20percent',
  text: 'Limited time First Fix offer! 20% off + waived styling fee',
  Icon: IconFixDelivery16
};
export const NoPromoIncentiveCopy = {
  id: 'AdultsFirstFixDiscountBanner-noPromo',
  text: 'First Fix offer! Waived styling fee',
  Icon: IconFixDelivery16
};
export const SUPTwentyDollarIncentiveCopy = {
  id: 'AdultsFirstFixDiscountBanner-SUP-20dollar',
  text: 'First Fix offer! $20 off + waived styling fee',
  Icon: IconFixDelivery16
};
export const useAdultsIncentiveBanner = ({
  onClick,
  onLoad,
  variant = 'promo-dark',
  isLoading,
  offer
}: UseIncentiveBannerProps): IncentiveBannerTypes => {
  const {
    asPath
  } = useRouter();
  const isMobileScreenSize = useScreenSize() === 'sm';
  const hasCalledOnLoad = useRef(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [closedBanners] = useLocalStorageValue<BannerLocalStorageData | undefined>(knitBannersLocalStorageKey, {}, {
    initializeWithStorageValue: false
  });
  const normalizedPath = asPath.split('?')[0];
  const isSUPFixRoute = supFixIncentiveBannerSupportedPaths.includes(normalizedPath);
  const copy = (() => {
    if (isSUPFixRoute) {
      return SUPTwentyDollarIncentiveCopy;
    }
    if (offer?.cell === 'no-promo') {
      return NoPromoIncentiveCopy;
    }
    if (offer?.cell === 'percentage') {
      return TwentyPercentIncentiveCopy;
    }
    return TwentyDollarIncentiveCopy;
  })();
  const {
    id,
    text,
    Icon
  } = copy;
  const hasBannerBeenClosed = closedBanners?.[id]?.closed;
  const shouldIncludeBanner = useMemo(() => {
    return primaryLandingPaths.includes(normalizedPath) || supFixIncentiveBannerSupportedPaths.includes(normalizedPath);
  }, [normalizedPath]);
  const isBannerShown = shouldIncludeBanner && !hasBannerBeenClosed && hasLoaded;
  useEffect(() => {
    // We don't know which one to set until the Experiment is loaded
    if (isLoading) return;
    if (!hasCalledOnLoad.current) {
      hasCalledOnLoad.current = true;
      setHasLoaded(true);
      onLoad?.();
    }
  }, [isLoading, onLoad]);
  if (!isBannerShown || isLoading) return {};
  const slimBanner: HeaderProps['slimBanner'] = {
    text,
    id,
    cta: {
      text: 'See details',
      onClick
    },
    variant,
    icon: isMobileScreenSize ? null : <Icon purpose="decorative" color="inherit" />
  };
  return {
    slimBanner
  };
};